/**
 * RunAuditPanel Component
 *
 * Shows recent runs with verification status.
 */

import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import DataBadge from './DataBadge';

interface RunRecord {
  id: string;
  case_id: string;
  solver_type: string;
  status: string;
  created_at: string;
  input_hash: string;
}

interface VerificationResult {
  run_id: string;
  verified: boolean;
  input_hash_match: boolean;
  output_hash_match: boolean;
  message: string;
}

const RunAuditPanel: React.FC = () => {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifications, setVerifications] = useState<Record<string, VerificationResult>>({});
  const [verifying, setVerifying] = useState<Record<string, boolean>>({});

  const fetchRuns = async () => {
    try {
      setLoading(true);
      const data = await api.getRuns();
      setRuns(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  const handleVerify = async (runId: string) => {
    setVerifying((prev) => ({ ...prev, [runId]: true }));
    try {
      const result = await api.verifyRun(runId);
      setVerifications((prev) => ({ ...prev, [runId]: result }));
    } catch (err) {
      setVerifications((prev) => ({
        ...prev,
        [runId]: {
          run_id: runId,
          verified: false,
          input_hash_match: false,
          output_hash_match: false,
          message: err instanceof Error ? err.message : 'Verification failed',
        },
      }));
    } finally {
      setVerifying((prev) => ({ ...prev, [runId]: false }));
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">Audit & Verification</h3>
          <DataBadge tier="SYSTEM" />
        </div>
        <button
          onClick={fetchRuns}
          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
        >
          Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-gray-400">Loading runs...</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}

      {!loading && !error && runs.length === 0 && (
        <div className="text-sm text-gray-400">No runs recorded yet.</div>
      )}

      <div className="space-y-2">
        {runs.slice(0, 6).map((run) => {
          const verification = verifications[run.id];
          const isVerifying = verifying[run.id];
          return (
            <div key={run.id} className="border border-gray-700 rounded-lg p-3">
              <div className="flex justify-between text-xs text-gray-400">
                <span>{run.id}</span>
                <span>{new Date(run.created_at).toLocaleTimeString()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-300 mt-1">
                <span>{run.solver_type}</span>
                <span className={run.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}>
                  {run.status}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate max-w-[70%]">
                  {run.input_hash}
                </span>
                <button
                  onClick={() => handleVerify(run.id)}
                  disabled={isVerifying}
                  className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:opacity-50"
                >
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              {verification && (
                <div className="mt-2 text-xs">
                  <span
                    className={
                      verification.verified ? 'text-green-400' : 'text-amber-300'
                    }
                  >
                    {verification.message}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RunAuditPanel;
